import { EnumDiscountType, EnumDiscountUnit } from "gql/generated";
import { formatPrice } from "shared/utils";

type NudgeDiscount = {
  type: EnumDiscountType;
  unit: EnumDiscountUnit;
  amount: number;
  isActive?: boolean | null;
  minQuantity?: number | null;
  minSubtotal?: number | null;
  buyQuantity?: number | null;
  getQuantity?: number | null;
};

export type CartDiscountNudge =
  | { kind: "quantity"; itemsNeeded: number; discount: string }
  | { kind: "spend"; amountNeeded: string; discount: string }
  | { kind: "bogo"; itemsNeeded: number; discount: string; isFree: boolean };

const formatDiscount = (d: NudgeDiscount) =>
  d.unit === EnumDiscountUnit.Percentage
    ? `${Math.round(d.amount * 100)}%`
    : `$${formatPrice(d.amount)}`;

/**
 * The closest unmet cart-wide discount, for an "add N more to save" hint.
 * Mirrors the backend triggers in `discount.service.ts`.
 */
export const getCartDiscountNudge = (
  discounts: NudgeDiscount[],
  units: number,
  subtotal: number,
): CartDiscountNudge | null => {
  if (units === 0) return null;
  const active = discounts.filter(d => d.isActive !== false);

  const nextVolume = active
    .filter(
      d => d.type === EnumDiscountType.Quantity && (d.minQuantity ?? 0) > units,
    )
    .sort((a, b) => a.minQuantity! - b.minQuantity!)[0];
  if (nextVolume) {
    return {
      kind: "quantity",
      itemsNeeded: nextVolume.minQuantity! - units,
      discount: formatDiscount(nextVolume),
    };
  }

  for (const d of active) {
    if (d.type !== EnumDiscountType.BuyXGetY) continue;
    const buy = d.buyQuantity ?? 0;
    const get = d.getQuantity ?? 0;
    if (buy < 1 || get < 1) continue;
    const inGroup = units % (buy + get);
    if (inGroup >= buy) {
      return {
        kind: "bogo",
        itemsNeeded: buy + get - inGroup,
        discount: formatDiscount(d),
        isFree: d.unit === EnumDiscountUnit.Percentage && d.amount >= 1,
      };
    }
  }

  const nextSpend = active
    .filter(
      d =>
        d.type === EnumDiscountType.SpendThreshold &&
        (d.minSubtotal ?? 0) > subtotal,
    )
    .sort((a, b) => a.minSubtotal! - b.minSubtotal!)[0];
  if (nextSpend) {
    return {
      kind: "spend",
      amountNeeded: `$${formatPrice(nextSpend.minSubtotal! - subtotal)}`,
      discount: formatDiscount(nextSpend),
    };
  }

  return null;
};
